//Library Imports
import React, { useState, useEffect, useRef } from "react";
import store, { RootState } from "store";
import { useSelector } from "react-redux";

//Local Imports
import TableSort from "modules/Common/Components/TableSort";
import { getConstantCampaignEmailId, setSortDirection } from "modules/Analytics/utils";
import { getContentEngagementList, resetContentEngagementList } from "./actions";
import { addCommaToNumber, getImageUrl, getQuarterDates, getQueryAccountId } from "utils";
import ContactListLoader from "modules/Analytics/Contacts/ContactListLoader";
import { DEFAULT_CAMPAIGN_IMAGE } from "constants/types";
import { RedirectToArticleSource } from "./utils";
import Speed from "../../../../assets/img/icons/Speed.svg";
import { analyticsDatesFilterChange } from "modules/Analytics/Dashboard/actions";

//Style Imports
import styled from "styled-components";
import { ANALYTICS_DEFAULT_DATES_FILTER } from "modules/Analytics/Contacts/utils";

interface IContentEngagementList {
  searchedText: string;
  setSearchedText: (value: string) => void;
}
const ContentEngagementList: React.FC<IContentEngagementList> = (props: IContentEngagementList) => {
  const DEFAULT_PAGE_LIMIT = 20;

  //Props
  const { searchedText, setSearchedText } = props;

  //Redux States
  const engagementList = useSelector((state: RootState) => state.accountsContentEngagement.data.content);
  const totalArticlesCount = useSelector(
    (state: RootState) => state.accountsContentEngagement.data.totalArticlesCount
  );
  const loading = useSelector((state: RootState) => state.accountsContentEngagement.loading);
  const loaded = useSelector((state: RootState) => state.accountsContentEngagement.loaded);
  const selectedCampaignsIds = useSelector(
    (state: RootState) => state.accountsContentEngagement.campaignIds ?? []
  );
  const selectedEmailIds = useSelector((state: RootState) => state.accountsContentEngagement.emailIds ?? []);
  const excludeCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeCampaignsIds);
  const excludeAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeAssetsIds);
  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns
  );
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters
  );

  //Local States
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState({ accessor: "", sortDirection: "none" });
  const [offset, setOffset] = useState(0);
  const [firstTime, setFirstTime] = useState(true);
  const [ImgWidth, setImgWidth] = useState<number>(0);
  const [ImgHeight, setImgHeight] = useState<number>(0);
  const ImgDimensionsRef = useRef<any>();
  const [showTopBtn, setShowTopBtn] = useState(false);

  const getPagination = (resetPagination?: boolean, resetSearch?: boolean, sortParams?: any) => {
    let analyticsGraph = store.getState().analyticsGraph;
    let analyticsDashboard = store.getState().analyticsDashboard;
    let { appliedWorkflowsIds } = analyticsDashboard;
    let { startDate, endDate } = analyticsGraph;

    if (startDate === "" && endDate === "") {
      analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
      startDate = getQuarterDates().start_date;
      endDate = getQuarterDates().end_date;
    }
    const orderBy = sortParams?.orderBy ?? sortType.sortDirection;

    // to update emails ids based on filters we need to clone the arrays
    let selected_emails_ids = [...selectedEmailIds];
    let selected_campaings_ids = [...selectedCampaignsIds];
    let selected_workflows_ids = [...appliedWorkflowsIds];

    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      selected_campaings_ids = [campaign_email_id.toString()];
      selected_emails_ids = [campaign_email_id.toString()];
    }

    if (resetPagination) {
      resetContentEngagementList();
    }

    let params: any = {
      allCampaigns: AllCampaignsFilterCheck,
      limit: DEFAULT_PAGE_LIMIT,
      offset: resetPagination ? 0 : engagementList?.length,
      sortBy: orderBy === "" || orderBy === "none" ? "" : sortParams?.sortBy ?? sortType.accessor,
      orderBy: orderBy,
      accountId: getQueryAccountId(),
      emailIds: selected_emails_ids, // selected emails ids
      startDate: startDate,
      endDate: endDate,
      campaignIds: selected_campaings_ids, // selected campaigns ids
      searchWord: resetSearch ? "" : searchedText,
      workflowIds: selected_workflows_ids
    };

    if (AllCampaignsFilterCheck) {
      params["excludeCampaignsIds"] = excludeCampaignsIds;
      params["excludeAssetsIds"] = excludeAssetsIds;
    }

    return params;
  };
  const sortedResults = (column?: any) => {
    let orderBy = "";
    if (column.canSort) {
      setSortBy(column.id);
      orderBy = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: orderBy });
      getContentEngagementList(getPagination(true, false, { sortBy: column.id, orderBy }));
    }
  };

  // This function calculates width and height of the list
  const getImgSize = () => {
    const newWidth = ImgDimensionsRef?.current?.clientWidth;
    setImgWidth(newWidth);

    const newHeight = ImgDimensionsRef?.current?.clientHeight;
    setImgHeight(newHeight);
  };

  useEffect(() => {
    if (firstTime) {
      setSearchedText("");
      getContentEngagementList(getPagination(true, true), setFirstTime);
      return () => {
        resetContentEngagementList();
      };
    }
    getImgSize();
    window.addEventListener("resize", getImgSize);
    window.addEventListener("scroll", () => {
      if (window.scrollY > 400) {
        setShowTopBtn(true);
      } else {
        setShowTopBtn(false);
      }
    });
    return () => {
      window.removeEventListener("resize", getImgSize);
      window.removeEventListener("scroll", () => {
        if (window.scrollY > 400) {
          setShowTopBtn(true);
        } else {
          setShowTopBtn(false);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!firstTime) {
      getContentEngagementList(getPagination(true));
    }
  }, [searchedText]);

  const columns = React.useMemo(
    () => [
      {
        Header: "Article Title",
        disableSortBy: true,
        accessor: "articleTitle",
        Cell: (props: any) => {
          let item = props.row.original;
          return (
            <div>
              <div className="preview">
                <div
                  className="company-overlay"
                  style={{
                    backgroundImage: `url(${item.articleImage !== null && item.articleImage !== ""
                      ? getImageUrl(
                        item.articleImage,
                        ImgWidth > 0 ? ImgWidth : 50,
                        ImgHeight > 0 ? ImgHeight : 50,
                        item.articleId
                      )
                      : DEFAULT_CAMPAIGN_IMAGE
                      })`,
                  }}
                ></div>
                <img
                  ref={ImgDimensionsRef}
                  src={
                    item.articleImage !== null && item.articleImage !== ""
                      ? getImageUrl(
                        item.articleImage,
                        ImgWidth > 0 ? ImgWidth : 50,
                        ImgHeight > 0 ? ImgHeight : 50,
                        item.articleId
                      )
                      : DEFAULT_CAMPAIGN_IMAGE
                  }
                  alt=""
                />
              </div>
              <div className="details">
                <>
                  <div className="article_title">{item.articleTitle}</div>
                  <div className="article-link">{item.articleLink}</div>
                </>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Email / Sent",
        id: "last_sent_date",
        accessor: "emailName",
        sortType: "basic",
        sortDirection: sortType.accessor === "last_sent_date" ? sortType.sortDirection : "none",
        canSort: true,
        Cell: (props: any) => {
          let item = props.row.original;
          return (
            <div className="details">
              <>
                <div className="email_name">{item.emailName}</div>
                <div className="email_sent_date">{item.emailSentDate}</div>
              </>
            </div>
          );
        },
      },
      {
        Header: "Clicks",
        id: "total_clicks",
        accessor: "totalClicks",
        sortType: "basic",
        sortDirection: sortType.accessor === "total_clicks" ? sortType.sortDirection : "none",
        canSort: true,
        Cell: (props: any) => {
          const item = props.row.original;
          return <div>{addCommaToNumber((item.totalClicks ?? 0).toString() ?? "")}</div>;
        },
      },
      {
        Header: "Click Date",
        id: "last_click_date",
        accessor: "lastClickDate",
        sortType: "basic",
        sortDirection: sortType.accessor === "last_click_date" ? sortType.sortDirection : "none",
        canSort: true,
        Cell: (props: any) => {
          const item = props.row.original;
          return (
            <div className="details">
              <div className="email_name">
                {item.lastClickDate.split(",")[0] + ", " + item.lastClickDate.split(",")[1]}
              </div>
              <div className="email_sent_date">{item.lastClickDate.split(",")[2]}</div>
            </div>
          );
        },
      },
      {
        Header: "Score",
        disableSortBy: true,
        accessor: "score",
        Cell: (props: any) => {
          let item = props.row.original;
          return (
            <div className="last-item">
              <div className="engagement">
                <img src={Speed} alt="" />
                &nbsp;
                {addCommaToNumber((item.score ?? 0).toString() ?? "")}
              </div>
            </div>
          );
        },
      },
    ],
    [sortType]
  );

  return (
    <Wrapper>
      {/* <InfiniteLoader
        dataLength={engagementList?.length}
        next={() => getContentEngagementList(getPagination())}
        hasMore={engagementList?.length < totalArticlesCount}
        loader={<ContactListLoader loading={loading} />}
      > */}
      <TableSort
        columns={columns}
        data={engagementList}
        module={"account_content"}
        _onCellClick={sortedResults}
        _onRowClick={RedirectToArticleSource}
        next={() => getContentEngagementList(getPagination())}
        hasMore={engagementList?.length < totalArticlesCount}
        loader={<ContactListLoader loading={loading} />}
        loading={loading}
        wrapperClassName={`engagement-list-wrapper  ${!loading && engagementList.length === 0 ? "no-data-width" : ""
          }`}
        noScrollTarget={true}
      />
      {showTopBtn ? (
        <div className="back-to-top" onClick={() => window.scrollTo(0, 0)}>
          <span className="up-arrow"></span>
          <span>top</span>
        </div>
      ) : null}

      {loaded && engagementList?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
      {/* {<ContactListLoader loading={loading} />} */}
      {/* </InfiniteLoader> */}
    </Wrapper>
  );
};

export default ContentEngagementList;

const Wrapper = styled.div`
  /* overflow: hidden; */
  width: inherit;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    min-height: 83px;
  }
  .back-to-top {
    /* position: absolute;
    right: 0;
    bottom: 0; */
    position: fixed;
    right: 25px;
    bottom: 85px;
    height: 70px;
    width: 70px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 14px -2px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    .up-arrow {
      border: solid #1ec252;
      border-width: 0 3px 3px 0;
      display: inline-block;
      padding: 3px;
      width: 15px;
      height: 15px;
      transform: rotate(-135deg);
      -webkit-transform: rotate(-135deg);
    }
    span {
      font-family: "Roboto", sans-serif;
      font-weight: 700;
      font-size: 14px;
      line-height: 1;
      text-transform: uppercase;
    }
  }
  .infinite-scroll-component__outerdiv {
    width: 100%;
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
  }
  .no-data {
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "robotomedium";
    color: #67696a;
    font-size: 1rem;
    margin-bottom: 30px;
    border-radius: 5px;
  }
  .no-data-width {
    height: auto !important;
  }
  .engagement-list-wrapper {
    width: 100%;
    /* height: calc(100vh - 200px); */
    height: auto;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      /* height: calc(100vh - 175px); */
    }
  }
  table {
    width: 100%;
    /* margin-top: 24px;
    display: block; */
    thead {
      border-bottom: 1px solid #dddddd;
      position: sticky;
      top: 120px;
      z-index: 999;
      background: #f9f8f6;
      z-index: 9;
      padding-top: 10px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        top: 90px;
      }
      tr {
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: "Roboto", sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      /* height: calc(100vh - 300px); */
      /* height: auto; */
      /* padding-bottom: 90px; */
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        /* height: calc(100vh - 250px); */
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      /* &::-webkit-scrollbar-thumb:hover {
        background: #efefef;
      } */
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
      }
    }
    thead {
      tr {
        padding: 0px 15px;
        th {
          span {
            width: 15px;

            .sort_asc {
              width: 10px;
              height: 8px;
              margin-top: -15px;
              margin-left: 5px;
              float: left;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: -15px;
              }
              img {
                transform: rotate(180deg);
              }
            }
            .sort_desc {
              width: 10px;
              height: 8px;
              float: left;
              margin-left: 5px;
              margin-top: 5px;
              img {
                float: left;
              }
            }
          }
        }
      }

      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          white-space: nowrap;
        }
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr {
        width: 100%;
        display: block;
        float: left;
        td {
          min-height: 70px;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          font-size: 20px;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &:nth-child(1) {
            width: calc(100% - 900px);
            max-width: 700px;
            /* max-width: calc(100vw - 1125px); */
            text-align: left;
            display: flex;
            @media all and (max-width: 1621px) {
              width: calc(100% - 800px);
            }
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: calc(100% - 510px);
              /* max-width: calc(100vw - 705px); */
            }
            > div {
              display: flex;
              width: 100%;
              flex-direction: row;
              align-items: start;
              align-content: space-evenly;
              min-height: 100%;
              max-width: 100%;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              .preview {
                float: left;
                align-self: center;
                border-radius: 10px;
                overflow: hidden;
                padding: 2px;
                position: relative;
                img {
                  border-radius: 10px;
                  /* outline: 2px solid #f0b520; */
                  box-shadow: #f0b520 0px 0px 0px 2px;
                  min-width: 51px;
                  min-height: 51px;
                  width: 51px;
                  height: 51px;
                }
                .company-overlay {
                  min-width: 51px;
                  min-height: 51px;
                  /* outline: 2px solid #f0b520; */
                  border-radius: 10px;
                  position: absolute;
                  left: 2px;
                  top: 2px;
                  background-size: cover;
                  background-color: transparent;
                  background-repeat: no-repeat;
                  background-position: center;
                  z-index: 5;
                }
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: "Roboto Slab", serif;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                align-self: center;
                max-width: calc(100% - 120px);
                padding-left: 15px;
                .article_title {
                  color: #000000;
                  font-weight: 500;
                  font-size: 16px;
                  font-family: "Roboto", sans-serif;
                  max-width: 100%;
                  display: -webkit-box;
                  display: block;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-break: break-word;
                  white-space: nowrap;
                }
                .article-link {
                  color: #1ec252;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  max-width: 100%;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  display: block;
                }
              }
            }
          }
          &:nth-child(2) {
            width: 320px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1621px) {
              width: 200px;
            }
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 140px;
            }
            > div {
              display: flex;
              width: 100%;
              flex-direction: column;
              align-items: start;
              align-content: space-evenly;
              min-height: 100%;
              .email_name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                word-break: keep-all;
                display: block;
                font-size: 16px;
                color: #424b50;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
              }
              .email_sent_date {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                word-break: keep-all;
                display: block;
                font-size: 14px;
                color: #424b50;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 10px;
                }
              }
            }
          }
          &:nth-child(3) {
            width: 160px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1621px) {
              width: 130px;
            }
          }
          &:nth-child(4) {
            width: 300px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            /* justify-content: center; */
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 160px;
            }
            > div {
              display: flex;
              width: 100%;
              flex-direction: column;
              align-items: start;
              align-content: space-evenly;
              min-height: 100%;
              .email_name {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                word-break: keep-all;
                display: block;
                font-size: 16px;
                color: #424b50;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
              }
              .email_sent_date {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                word-break: keep-all;
                display: block;
                font-size: 14px;
                color: #424b50;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 10px;
                }
              }
            }
          }
          &:nth-child(5) {
            width: 120px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 80px;
            }
          }
        }
      }
    }
  }
`;
